/**
 * Active Hand screen (04) — per wireframe `04_active_hand_*`.
 *
 * Wireframe-matching layout:
 *   - Ellipse-shaped felt (not rounded rect)
 *   - Villain at top of felt: cards + avatar in one row, pill below, stack line below pill
 *   - Board cards centered in the felt
 *   - Pot displayed BELOW board: large dollar amount with small "POT" label
 *   - Hero at bottom of felt: pill on top, stack line below pill, then cards + avatar
 *   - 4-slot action panel at the bottom (per ActionPanel)
 *
 * Per-element coordinates from /design/wireframes/excalidraw/04_active_hand/04_active_hand_mobile.excalidraw.
 *
 * Not in this slice: timer (4d), REC indicator (5), dealer-button "D" detail,
 * full Action Panel decision-tree visualization (V1 Live Mode per PRD §9).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { ActionLog } from '@/components/ActionLog';
import { ActionPanel } from '@/components/ActionPanel';
import { MobileDrawer } from '@/components/MobileDrawer';
import { PokerTable } from '@/components/PokerTable';
import { Timer } from '@/components/Timer';
import { sessionManager } from '@/services/sessionManager';
import { recordingPipeline } from '@/services/recordingPipeline';
import { whisperAdapter } from '@/services/whisperAdapter';
import { isTerminal } from '@/lib/engine/stateMachine';
import type { PlayerAction } from '@/types/action';
import type { HandState } from '@/types/handState';
import type { Session } from '@/types/session';
import { cn } from '@/lib/utils';

/**
 * Per-frame delay during animated hand progression. Should match the
 * announcement duration in PokerTable.tsx (ANNOUNCEMENT_MS) so each action's
 * "CHECK / BET $X / etc." has time to be read before the next frame lands.
 */
const FRAME_MS = 1500;

export function ActiveHandScreen() {
  const [session, setSession] = useState<Session | null>(null);
  const [loaded, setLoaded] = useState(false);

  // Pending frame timers for the current animation. Cleared on new action or
  // unmount so we never apply stale frames over a fresh state.
  const pendingTimersRef = useRef<number[]>([]);

  const clearPendingTimers = useCallback(() => {
    pendingTimersRef.current.forEach((id) => clearTimeout(id));
    pendingTimersRef.current = [];
  }, []);

  /**
   * Play a sequence of HandState frames with FRAME_MS between each. Each
   * scheduled frame composes the frame as `{...finalSession, activeHand: frame}`
   * so session-level fields stay consistent (resolved config, etc.).
   */
  const playFrames = useCallback(
    (frames: HandState[], finalSession: Session) => {
      clearPendingTimers();
      frames.forEach((frame, i) => {
        const id = window.setTimeout(() => {
          setSession({ ...finalSession, activeHand: frame });
        }, i * FRAME_MS);
        pendingTimersRef.current.push(id);
      });
    },
    [clearPendingTimers],
  );

  useEffect(() => {
    setSession(sessionManager.loadActiveSession());
    setLoaded(true);
    // On unmount, cancel any pending frames.
    return () => clearPendingTimers();
  }, [clearPendingTimers]);

  const handleAction = useCallback(
    (action: PlayerAction, elapsedMs?: number) => {
      // Capture targeting info BEFORE applyHeroAction mutates session.
      // The hero's action will land at index = current history.length
      // (per stateMachine.ts: ActionRecord.index = state.history.length
      // at apply-time).
      const handId = session?.activeHand?.handId;
      const actionIndex = session?.activeHand?.history.length;

      const { session: finalSession, frames } =
        sessionManager.applyHeroAction(action, elapsedMs);
      playFrames(frames, finalSession);

      // Background: stop recording → transcribe → attach transcript.
      // Runs in parallel with the frame animation; the typical 1.5s+
      // animation gives Whisper plenty of time on cached models.
      if (handId !== undefined && actionIndex !== undefined) {
        void (async () => {
          try {
            const blob = await recordingPipeline.stop();
            if (blob.size === 0) return;
            const result = await whisperAdapter.transcribe(blob);
            if (result.text) {
              sessionManager.attachTranscript(
                handId,
                actionIndex,
                result.text,
              );
            }
          } catch {
            // Best-effort. PRD §10.2 — grade pipeline runs on whatever
            // transcript exists; missing transcripts are acceptable.
          }
        })();
      }
    },
    [playFrames, session],
  );

  if (!loaded) return null;
  if (!session) return <Navigate to="/" replace />;
  if (!session.activeHand) return <Navigate to="/config" replace />;

  return (
    <ActiveHandLayout
      session={session}
      onAction={handleAction}
    />
  );
}

// =============================================================================
// Layout
// =============================================================================

function ActiveHandLayout({
  session,
  onAction,
}: {
  session: Session;
  onAction: (action: PlayerAction, decisionElapsedMs?: number) => void;
}) {
  const navigate = useNavigate();
  const state = session.activeHand!;
  const resolved = session.activeHandResolved;
  const archetypeLabel = resolved?.archetype ?? 'Fish';
  const terminal = isTerminal(state);

  // Auto-navigate to /resolution once the hand terminates. We give a brief
  // delay (longer than the fold-animation duration) so the final on-table
  // state — folded cards faded, last announcement settled — is visible
  // before we transition. PRD §8.3: Resolution → Grade transition itself
  // is owned by the Resolution screen.
  useEffect(() => {
    if (!terminal) return;
    const id = window.setTimeout(() => navigate('/resolution'), 1500);
    return () => clearTimeout(id);
  }, [terminal, navigate]);

  // Timer: active while hero is to-act on an open street, doesn't force action
  const isHeroTurnOpen =
    state.toAct === 'hero' && state.phase === 'street_open' && !terminal;
  const timerSeconds = session.config.timerSeconds; // null = no timer

  // Audio recording auto-starts when hero is to-act on an open street
  // (PRD §8.2 step 2). handleAction owns the STOP path so it can pipe the
  // resulting Blob into WhisperAdapter. This effect only kicks off start
  // (idempotent at the pipeline level — concurrent calls share one
  // startPromise) and toggles the REC indicator. The unmount safety net
  // below catches any leak.
  //
  // Notably we do NOT cancel an in-flight start from the cleanup. A user
  // confirming a fast action runs cleanup before start() resolves; if we
  // cancelled, handleAction.stop() would land on an inactive pipeline and
  // return an empty Blob. By letting start finish, the pipeline's stop()
  // (which now awaits startPromise) gets a real recording to flush.
  const [isRecording, setIsRecording] = useState(false);
  // Wall-clock timestamp when the current hero turn became open. Used
  // to compute decisionElapsedMs when the user confirms an action.
  const decisionStartRef = useRef<number | null>(null);
  useEffect(() => {
    if (!isHeroTurnOpen) return;
    decisionStartRef.current = Date.now();
    void recordingPipeline
      .start()
      .then(() => setIsRecording(true))
      .catch(() => {
        // Mic unavailable / permission revoked mid-session. REC stays off;
        // action panel still works.
      });
    return () => {
      // Turn off the indicator immediately when the hero's turn ends.
      // Pipeline state (active recorder, in-flight start) is unchanged
      // here — handleAction.stop() or the unmount safety effect will
      // resolve it.
      setIsRecording(false);
    };
  }, [isHeroTurnOpen]);

  // Unmount safety net: if the user navigates away mid-recording, drop
  // the mic so the OS doesn't keep showing a recording indicator.
  useEffect(() => {
    return () => {
      if (recordingPipeline.isRecording()) {
        recordingPipeline.cancel();
      }
    };
  }, []);

  // Mobile drawer state — at most one open at a time. Desktop unaffected.
  const [openDrawer, setOpenDrawer] = useState<'stats' | 'panel' | null>(null);
  const openStats = useCallback(() => setOpenDrawer('stats'), []);
  const openPanel = useCallback(() => setOpenDrawer('panel'), []);
  const closeDrawer = useCallback(() => setOpenDrawer(null), []);

  return (
    <main className="flex min-h-dvh flex-col bg-surface-app text-text-primary">
      <Header
        potType={state.potType}
        heroPosition={state.heroPosition}
        archetype={archetypeLabel}
        timerSeconds={timerSeconds}
        timerActive={isHeroTurnOpen}
        timerResetKey={`${state.handId}-${state.history.length}-${state.street}`}
        isRecording={isRecording}
      />

      {/* Table + sidebars row */}
      <div className="flex flex-1 overflow-hidden">
        {/* Stats sidebar (left). Mobile: thin strip → tap opens drawer.
            Desktop: full width for future session-stats content. */}
        <StatsSidebar
          isOpen={openDrawer === 'stats'}
          onOpen={openStats}
          onClose={closeDrawer}
        />

        {/* Table (center). Flex-centers the table both axes; the table
            itself uses min() sizing to never push past the viewport. */}
        <div className="flex min-h-0 min-w-0 flex-1 items-center justify-center px-3 py-3">
          <PokerTable state={state} archetypeLabel={archetypeLabel} />
        </div>

        {/* Action Panel sidebar (right). Mobile: thin strip → tap opens
            drawer. Desktop: full action history inline. */}
        <ActionLog
          state={state}
          resolved={resolved}
          isOpen={openDrawer === 'panel'}
          onOpen={openPanel}
          onClose={closeDrawer}
        />
      </div>

      {/* Bottom bar — action buttons, waiting line, or transitional
          "hand complete" while the auto-nav to /resolution fires. */}
      {terminal ? (
        <div className="flex min-h-[5rem] items-center justify-center border-t border-border bg-surface-elevated px-4 text-center text-xs uppercase tracking-wider text-text-muted">
          Hand complete · loading resolution…
        </div>
      ) : state.toAct === 'hero' ? (
        <ActionPanel
          state={state}
          onAction={(action) => {
            // Compute elapsed ms from when this hero turn became open
            // (decisionStartRef set by the recording useEffect above).
            // Reset the ref so the next hero turn starts fresh.
            const elapsedMs =
              decisionStartRef.current !== null
                ? Date.now() - decisionStartRef.current
                : undefined;
            decisionStartRef.current = null;
            onAction(action, elapsedMs);
          }}
        />
      ) : (
        <div className="flex min-h-[5rem] items-center justify-center border-t border-border bg-surface-elevated px-4 text-center text-xs text-text-muted">
          Waiting on {state.toAct}…
        </div>
      )}
    </main>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function Header({
  potType,
  heroPosition,
  archetype,
  timerSeconds,
  timerActive,
  timerResetKey,
  isRecording,
}: {
  potType: HandState['potType'];
  heroPosition: HandState['heroPosition'];
  archetype: string;
  timerSeconds: number | null;
  timerActive: boolean;
  timerResetKey: string;
  isRecording: boolean;
}) {
  return (
    <div className="shrink-0 border-b border-border bg-surface-app px-4 py-2">
      <div className="mx-auto flex max-w-3xl flex-col gap-1.5 text-xs">
        {/* Row 1: situation crumbs (centered) */}
        <div className="flex items-center justify-center">
          <div className="text-text-secondary">
            {potType} <span className="text-text-muted">·</span> PFR{' '}
            <span className="text-text-muted">·</span> {heroPosition}{' '}
            <span className="text-text-muted">·</span> {archetype}
          </div>
        </div>
        {/* Row 2: numeric timer (left) · countdown bar (flex middle) · REC
            indicator (right). Pause/menu icon is V2 per PRD §17 / §8.4 and
            intentionally not rendered in V1. */}
        <div className="flex items-center gap-3">
          <Timer
            initialSeconds={timerSeconds}
            active={timerActive}
            resetKey={timerResetKey}
          />
          <RecIndicator active={isRecording} />
        </div>
      </div>
    </div>
  );
}

/**
 * Recording indicator (the ● REC from the wireframe). Red filled dot +
 * "REC" label, pulses while recording is active. Greyed out when inactive
 * so its absence from the header doesn't shift layout.
 */
function RecIndicator({ active }: { active: boolean }) {
  return (
    <div
      className={cn(
        'flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider',
        active ? 'text-state-error' : 'text-text-muted opacity-40',
      )}
      aria-label={active ? 'Recording' : 'Not recording'}
    >
      <span
        className={cn(
          'inline-block h-2 w-2 rounded-full bg-current',
          active && 'animate-pulse',
        )}
      />
      <span>REC</span>
    </div>
  );
}

/**
 * StatsSidebar — placeholder for future session-stats panel (hands played,
 * grade rollups, etc.). On mobile it collapses to a thin strip with a
 * vertical "STATS" label per wireframe `04_active_hand_mobile`. Tapping the
 * strip opens a slide-out drawer with the (currently empty) panel content.
 * Desktop (sm+) reserves a 16rem-wide column to fill in later.
 */
function StatsSidebar({
  isOpen,
  onOpen,
  onClose,
}: {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
}) {
  return (
    <>
      <aside
        className="flex w-10 shrink-0 flex-col items-center border-r border-border bg-surface-app sm:w-64"
        aria-label="Session stats"
      >
        {/* Mobile-only collapsed strip — tappable to open the drawer. */}
        <button
          type="button"
          onClick={onOpen}
          className={cn(
            'flex flex-1 items-center justify-center sm:hidden',
            'focus:outline-none focus:ring-2 focus:ring-inset focus:ring-border-focus',
          )}
          aria-label="Open session stats"
          aria-expanded={isOpen}
        >
          <span
            className="text-[10px] font-bold uppercase tracking-[0.3em] text-text-muted"
            style={{ writingMode: 'vertical-rl' }}
          >
            Stats
          </span>
        </button>
        {/* Desktop: future stats panel content. Intentionally empty in V1. */}
        <div className="hidden flex-1 sm:block" />
      </aside>

      {/* Mobile drawer — slides in from the left when isOpen */}
      <MobileDrawer
        isOpen={isOpen}
        onClose={onClose}
        side="left"
        title="Session stats"
      >
        <div className="p-4 text-sm text-text-muted">
          Session stats are coming soon.
        </div>
      </MobileDrawer>
    </>
  );
}


